/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import { contractorsAPI } from '../../services/api';
import { useBilling } from '../billing/BillingContext';
import { LockedPage } from '../billing/UpgradePrompts';

const TRADES = ['Carpentry', 'Cleaning', 'Electrical', 'Flooring', 'Gas', 'General Maintenance', 'Landscaping', 'Letting Agent', 'Plastering', 'Painting & Decorating', 'Pest Control', 'Plumbing', 'Roofing', 'Other'];

const defaultForm = { name: '', contact_name: '', trade: '', email: '', mobile: '', landline: '', address: '', notes: '', active: true };

export default function Contractors() {
  const { canAccess } = useBilling();
  if (!canAccess('contractors')) return <LockedPage feature="contractors" />;
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [filterTrade, setFilterTrade] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(defaultForm);
  const [saving, setSaving]           = useState(false);
  const [formError, setFormError]     = useState('');

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await contractorsAPI.getAll();
      setContractors(data);
    } catch (e) { setError('Failed to load contractors'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    let list = contractors;
    if (!showInactive) list = list.filter(c => c.active);
    if (filterTrade)   list = list.filter(c => c.trade === filterTrade);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.trade || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [contractors, showInactive, filterTrade, search]);

  const trades = useMemo(() => [...new Set(contractors.filter(c => c.trade).map(c => c.trade))].sort(), [contractors]);

  const openAdd = () => {
    setEditing(null);
    setForm(defaultForm);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, contact_name: c.contact_name||'', trade: c.trade||'', email: c.email||'', mobile: c.mobile||'', landline: c.landline||'', address: c.address||'', notes: c.notes||'', active: c.active });
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim()) { setFormError('Contractor name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        const result = await contractorsAPI.update(editing.id, form);
        setContractors(prev => prev.map(c => c.id === editing.id ? result : c));
      } else {
        const result = await contractorsAPI.create(form);
        setContractors(prev => [result, ...prev]);
      }
      setModalOpen(false);
    } catch (e) { setFormError('Failed to save contractor'); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (c) => {
    if (!window.confirm(`Deactivate ${c.name}? They will no longer appear in contractor lists.`)) return;
    try {
      await contractorsAPI.deactivate(c.id);
      setContractors(prev => prev.map(x => x.id === c.id ? { ...x, active: false } : x));
    } catch (e) { alert('Failed to deactivate contractor'); }
  };

  const handleReactivate = async (c) => {
    try {
      const result = await contractorsAPI.update(c.id, { ...c, active: true });
      setContractors(prev => prev.map(x => x.id === c.id ? result : x));
    } catch (e) { alert('Failed to reactivate contractor'); }
  };

  const activeCount   = contractors.filter(c => c.active).length;
  const inactiveCount = contractors.filter(c => !c.active).length;

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Contractors</h1>
          <p className="page-subtitle">{activeCount} active contractor{activeCount !== 1 ? 's' : ''}{inactiveCount > 0 ? ` · ${inactiveCount} inactive` : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Contractor</button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="form-control" style={{ maxWidth: '260px' }}
            placeholder="Search contractors..."
            value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-control" style={{ maxWidth: '200px' }}
            value={filterTrade} onChange={e => setFilterTrade(e.target.value)}>
            <option value="">All trades</option>
            {trades.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {filtered.length} contractor{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Loading / Error */}
      {loading && <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Loading contractors...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Contractors grid */}
      {!loading && filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span>🔧</span>
            <h3>No contractors yet</h3>
            <p>Add your trusted tradespeople to assign them to maintenance jobs</p>
            <button className="btn btn-primary" onClick={openAdd}>+ Add First Contractor</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {filtered.map(c => (
            <div key={c.id} className="card" style={{ borderLeft: `4px solid ${c.active ? 'var(--success)' : 'var(--border)'}`, opacity: c.active ? 1 : 0.7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{c.name}</div>
                  {c.contact_name && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.1rem' }}>Contact: {c.contact_name}</div>}
                  {c.trade && (
                    <span style={{ fontSize: '0.75rem', background: 'var(--primary)', color: '#fff', borderRadius: '0.25rem', padding: '0.1rem 0.5rem', fontWeight: 600, marginTop: '0.25rem', display: 'inline-block' }}>
                      {c.trade}
                    </span>
                  )}
                  {!c.active && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: 'var(--text-secondary)', background: 'var(--bg-light)', border: '1px solid var(--border)', borderRadius: '0.25rem', padding: '0.1rem 0.35rem' }}>Inactive</span>}
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                    onClick={() => openEdit(c)}>✏️</button>
                  {c.active
                    ? <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', color: 'var(--danger)' }}
                        onClick={() => handleDeactivate(c)}>Deactivate</button>
                    : <button className="btn btn-secondary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', color: 'var(--success)' }}
                        onClick={() => handleReactivate(c)}>Reactivate</button>
                  }
                </div>
              </div>
              <div style={{ display: 'grid', gap: '0.35rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                {c.mobile && <div>📱 {c.mobile}</div>}
                {c.landline && <div>☎️ {c.landline}</div>}
                {c.email && <div>✉️ {c.email}</div>}
                {c.address && <div>📍 {c.address}</div>}
                {c.notes && <div style={{ marginTop: '0.5rem', color: 'var(--text-primary)', fontStyle: 'italic', fontSize: '0.8rem' }}>"{c.notes}"</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? `✏️ Edit — ${editing.name}` : '+ Add Contractor'}</span>
              <button className="modal-close" onClick={() => setModalOpen(false)}>×</button>
            </div>

            {formError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{formError}</div>}

            <form onSubmit={handleSave}>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Company Name *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Smith Plumbing Ltd" required />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Contact Name</label>
                  <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="e.g. John Smith" />
                </div>
                <div className="form-group">
                  <label>Trade</label>
                  <select value={form.trade} onChange={e => set('trade', e.target.value)}>
                    <option value="">Select trade...</option>
                    {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contractor@email.com" />
                </div>
                <div className="form-group">
                  <label>Mobile</label>
                  <input value={form.mobile} onChange={e => set('mobile', e.target.value)} placeholder="e.g. 07700 900000" />
                </div>
                <div className="form-group">
                  <label>Landline</label>
                  <input value={form.landline} onChange={e => set('landline', e.target.value)} placeholder="e.g. 0191 000 0000" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Address</label>
                  <textarea value={form.address} onChange={e => set('address', e.target.value)} rows={3}
                    placeholder="Business address" style={{ width: '100%', resize: 'vertical' }} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                    placeholder="e.g. Preferred plumber — always available weekends" style={{ width: '100%', resize: 'vertical' }} />
                </div>
                {editing && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} />
                      Active — appears in contractor assignment lists
                    </label>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? '✅ Save Changes' : '✅ Add Contractor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
